import { KeyboardEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { INVITE_CONTACTS_LIMIT, INVITE_USER_IDS_LIMIT } from '@vyce/core/src/api/types';
import { DeviceContext, UniversalInviteContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useQuery } from '@vyce/core/src/hooks';
import { usePhoneListPaste } from '@vyce/core/src/hooks/usePhoneListPaste';

import { INVITE_MODULES } from '../constants';
import { InviteModuleKey, InviteStep, ResourceOption, WorkersFilter } from '../types';
import { WorkerInvitePrefill, workerInvitePrefill } from '../prefill';
import { buildWorkerInvitesPayload } from '../utils/buildWorkerInvitesPayload';
import { useInviteResources } from './useInviteResources';
import { useInviteSelection } from './useInviteSelection';
import { useInviteTargets } from './useInviteTargets';

interface Form {
  phone: string;
}

const EMPTY_FILTER: WorkersFilter = { module: null, resourceId: null };

const toOptions = (items?: { uuid?: string; name: string }[]): ResourceOption[] =>
  (items ?? []).filter(item => !!item.uuid).map(item => ({ id: item.uuid as string, name: item.name }));

export const useInviteWorkersDialog = () => {
  const {
    open,
    companyId,
    fetchResources,
    isLegend = false,
    disabledContactInput = false,
    preselectedTarget,
    onClose,
    createInvitesRequest,
    onSuccess,
  } = useContext(UniversalInviteContext);
  const { isMobile } = useContext(DeviceContext);
  const { handleServerError } = useContext(NotificationContext);
  const query = useQuery();
  const history = useHistory();

  const methods = useForm<Form>({ mode: 'all', defaultValues: { phone: '' } });
  const { reset, setValue, trigger, getValues } = methods;

  const [step, setStep] = useState<InviteStep>('who');
  const [tab, setTab] = useState<number>(0);
  const [filter, setFilter] = useState<WorkersFilter>(EMPTY_FILTER);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSuccessDialogOpen, setSuccessDialogOpen] = useState<boolean>(false);
  // The 202's own message — display-ready, its wording follows send_report
  const [successMessage, setSuccessMessage] = useState<string>('');
  // An invite prepared elsewhere (a Laila card), taken when the dialog opens
  const [prefill, setPrefill] = useState<WorkerInvitePrefill | null>(null);

  // Loaded on every open — the Redux company data can be stale or half-loaded
  const {
    teams,
    locations,
    paySchedules,
    loaded: resourcesReady,
  } = useInviteResources({
    companyId,
    open,
    fetchResources,
  });
  const selection = useInviteSelection();
  const targets = useInviteTargets({
    teams,
    locations,
    paySchedules,
    resourcesReady,
    open,
    preselect: preselectedTarget,
    prefill: prefill?.targets,
  });
  const { counts, reset: resetSelection } = selection;
  const { reset: resetTargets } = targets;

  // Filter options come from every resource the company has — filtering is about finding
  // people, so it is not limited by the caller's add rights (targets are)
  const resourcesByModule = useMemo<Record<InviteModuleKey, ResourceOption[]>>(
    () => ({
      teams: toOptions(teams),
      time: toOptions(locations),
      payroll: toOptions(paySchedules),
    }),
    [teams, locations, paySchedules]
  );
  const filterModules = useMemo(
    () => INVITE_MODULES.filter(config => resourcesByModule[config.key].length > 0),
    [resourcesByModule]
  );
  const resourceOptions = filter.module ? resourcesByModule[filter.module] : [];

  const bulkPaste = usePhoneListPaste({
    existing: selection.state.phones,
    onAdd: selection.addPhones,
    limit: INVITE_CONTACTS_LIMIT,
  });

  const addPhoneToList = async () => {
    // Use what trigger returns — `errors` from the render closure is a tick behind it, and
    // the ?phone= deep link sets a value without validating it
    const isValid = await trigger('phone');
    const phone = getValues('phone');
    if (!isValid || !phone) {
      return;
    }
    selection.addPhones([phone]);
    setValue('phone', '');
    // The paste counts describe an earlier action and are stale now
    bulkPaste.clear();
  };

  const handlePhoneFieldKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addPhoneToList();
    }
  };

  const { clear: clearPasteResult } = bulkPaste;

  const resetAll = useCallback(() => {
    reset();
    resetSelection();
    resetTargets();
    clearPasteResult();
    setStep('who');
    setTab(0);
    setFilter(EMPTY_FILTER);
  }, [reset, resetSelection, resetTargets, clearPasteResult]);

  const handleClose = () => {
    onClose();
    resetAll();
    // ?invite=1 is a deep link that opened the dialog — drop it so a refresh doesn't reopen it
    if (query.has('invite')) {
      history.push({ pathname: history.location.pathname });
    }
  };

  const limitError = useMemo(() => {
    if (counts.phones > INVITE_CONTACTS_LIMIT) {
      return `You can add up to ${INVITE_CONTACTS_LIMIT} new contacts per invite.`;
    }
    if (counts.picked > INVITE_USER_IDS_LIMIT) {
      return `You can pick up to ${INVITE_USER_IDS_LIMIT} existing users per invite — use "Select all" for more.`;
    }
    return null;
  }, [counts.phones, counts.picked]);

  const canGoNext = !!companyId && counts.total > 0 && !limitError && !targets.noModulesAvailable;

  const invite = async () => {
    try {
      setLoading(true);
      const payload = buildWorkerInvitesPayload(selection.state, targets.state);
      // 202 — the run continues in the background; the initiator gets the summary in Vyce messages.
      // A repeat invite is a 202 too: the person's older pending invite shows as replaced.
      const res = await createInvitesRequest({ companyId, payload });
      setSuccessMessage(res?.data?.message ?? '');
      setSuccessDialogOpen(true);
      onSuccess?.();
      handleClose();
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoading(false);
    }
  };

  const summaryText = useMemo(() => {
    if (counts.total === 0) {
      return null;
    }
    const people = counts.total === 1 ? 'person' : 'people';
    const prefix = counts.isEstimate ? 'Up to ' : '';
    const breakdown =
      counts.existing > 0 && counts.phones > 0 ? ` · ${counts.existing} existing, ${counts.phones} new` : '';
    return { total: `${prefix}${counts.total} ${people}`, breakdown };
  }, [counts]);

  useEffect(() => {
    const phone = query.get('phone');
    if (phone) {
      setValue('phone', phone);
    }
  }, [query, setValue]);

  // Opened with a prepared invite: its people are in, so it starts on where they go
  const { addPhones } = selection;
  useEffect(() => {
    if (!open) {
      setPrefill(null);
      return;
    }
    const next = workerInvitePrefill.take();
    if (next) {
      setPrefill(next);
      addPhones(next.phones);
      if (next.phones.length) {
        setStep('where');
      }
    }
  }, [open, addPhones]);

  // Company changed while open (Legend company picker) — selections belong to the old company
  const previousCompanyIdRef = useRef(companyId);
  useEffect(() => {
    if (previousCompanyIdRef.current !== companyId) {
      previousCompanyIdRef.current = companyId;
      resetAll();
    }
  }, [companyId, resetAll]);

  // Everyone was removed from the summary on step 2 — nothing left to place
  useEffect(() => {
    if (step === 'where' && counts.total === 0) {
      setStep('who');
    }
  }, [step, counts.total]);

  return {
    open,
    isMobile,
    isLegend,
    companyId,
    step,
    tab,
    setTab,
    filter,
    setFilter,
    filterModules,
    resourceOptions,
    methods,
    loading,
    isSuccessDialogOpen,
    successMessage,
    closeSuccessDialog: () => setSuccessDialogOpen(false),
    selection,
    targets,
    counts,
    summaryText,
    limitError,
    canGoNext,
    contactsDisabled: disabledContactInput || !companyId,
    addPhoneToList,
    handlePhoneFieldKeyDown,
    bulkPaste: {
      result: bulkPaste.result,
      onPasteList: bulkPaste.handlePasteList,
      onDismiss: bulkPaste.dismiss,
    },
    goNext: () => {
      // The summary belongs to step 1; don't leave it waiting behind you
      bulkPaste.clear();
      setStep('where');
    },
    goBack: () => setStep('who'),
    handleClose,
    invite,
  };
};

export type InviteWorkersDialogApi = ReturnType<typeof useInviteWorkersDialog>;
